import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaDatabase, FaSearch, FaUserFriends } from "react-icons/fa";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

function Features() {
  const featureData = [
    {
      icon: FaDatabase,
      title: "Comprehensive XP Database",
      description:
        "Access a detailed database containing epidemiological, genetic, clinical data and more.",
    },
    {
      icon: FaSearch,
      title: "Advanced Search and Analytics",
      description:
        "Leverage powerful search tools to find specific XP-related data, visualize genetic variations, clinical details, and connect to other specialized databases with ease.",
    },
    {
      icon: FaUserFriends,
      title: "User-Friendly Interface",
      description:
        "Designed for medical professionals, researchers, and decision makers, the database offers easy access to genetic data and research on XP, providing valuable insights for everyone.",
    },
  ];

  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.75)",
    "rgba(15, 23, 42, 0.75)"
  );
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const headingColor = useColorModeValue("slate.800", "white");
  const textColor = useColorModeValue("slate.600", "slate.300");

  return (
    <Box
      id="features"
      as="section"
      py={12}
      position="relative"
    >
      <Box mb={10} textAlign="center">
        <MotionHeading
          size="xl"
          textAlign="center"
          color={headingColor}
          fontWeight="900"
          letterSpacing="-0.02em"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Why Use the{" "}
          <Text as="span" bgGradient="linear(to-r, teal.500, cyan.400)" bgClip="text">
            Tunisian XP Database?
          </Text>
        </MotionHeading>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} maxW="1200px" mx="auto" px={6}>
        {featureData.map((feature, index) => (
          <MotionBox
            key={index}
            p={8}
            bg={cardBg}
            backdropFilter="blur(20px)"
            borderRadius="24px"
            border="1px solid"
            borderColor={cardBorder}
            shadow={useColorModeValue("0 10px 30px rgba(0,0,0,0.05)", "0 10px 30px rgba(0,0,0,0.3)")}
            textAlign="center"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: index * 0.15 }}
            whileHover={{ y: -8, shadow: "0 20px 40px rgba(13, 148, 136, 0.2)" }}
          >
            <Flex
              w="64px"
              h="64px"
              borderRadius="20px"
              bgGradient="linear(to-br, teal.400, cyan.500)"
              color="white"
              align="center"
              justify="center"
              mx="auto"
              mb={6}
              shadow="0 8px 20px rgba(13, 148, 136, 0.4)"
            >
              <Icon as={feature.icon} boxSize="28px" />
            </Flex>

            <Heading size="md" color={headingColor} mb={3} fontWeight="700">
              {feature.title}
            </Heading>

            <Text color={textColor} fontSize="sm" lineHeight="1.6">
              {feature.description}
            </Text>
          </MotionBox>
        ))}
      </SimpleGrid>

      <Divider my={12} maxW="1200px" mx="auto" borderColor={cardBorder} />
    </Box>
  );
}

export default Features;

